import { useState, useCallback, useRef, useEffect } from "react";
import type { InputHTMLAttributes } from "react";
import SearchIcon from "@/assets/SearchIcon";

interface SearchBarProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  icon?: React.ReactNode;
  autoFocus?: boolean;
  debounceMs?: number;
}

export default function SearchBar({
  value: controlledValue,
  onChange,
  onSearch,
  placeholder = "Search...",
  wrapperClassName = "",
  inputClassName = "",
  icon,
  autoFocus = false,
  debounceMs = 0,
  ...inputProps
}: SearchBarProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState("");
  const currentValue = isControlled ? controlledValue : internalValue;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;

      if (!isControlled) setInternalValue(val);

      if (debounceMs > 0) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onChange?.(val), debounceMs);
      } else {
        onChange?.(val);
      }
    },
    [isControlled, onChange, debounceMs],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        onSearch?.(currentValue);
      }
    },
    [onSearch, currentValue],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className={`search_bar ${wrapperClassName}`}>
      {icon !== undefined ? icon : <SearchIcon />}
      <input
        type="search"
        placeholder={placeholder}
        value={currentValue}
        onChange={handleChange}
        onKeyDown={onSearch ? handleKeyDown : undefined}
        autoFocus={autoFocus}
        {...inputProps}
      />
    </div>
  );
}
